import axios, { CancelTokenSource } from 'axios';
import { EndPoints, getParams, Http, TypeParams } from '../utils';
import { ItemInfo } from '../../typescript/itemInfo';
import { Note } from '../../typescript/notes/notes';
import { SatellitalImagesApiList } from '../../typescript/satellital/satellital_images';
import { NDVIInfo, SatellitalLayersInfo, ZonificationInfo } from '../../typescript/satellital/layers';
import { RanchVariableData, RanchVariablesCategorized, SatellitalChartStatistics, SatellitalValues } from '../../typescript/satellital/evolution_chart';
import { TablePaginatedFilters } from '../components/Generics/Table/SwTable/Table';
import { parseParametersToSendBackend } from './paramsSerializer';
import { FeatureCollection } from '../../typescript/MapShapes';

export interface ClaimItem {
    id: number;
    creation_date: string;
    claim_id: string;
    event_type: number;
    event_date: string;
    declared_area: number;
    area: number;
    polygon: any;
    ranch: {
        id: number;
        name: string;
        customer?: number;
    }
}

export interface ClaimDetailResponse {
    id: number;
    name: string;
    claim_id: string;
    creation_date: string;
    event_type: number;
    event_date: string;
    crop_type: number;
    declared_area: number;
    area: number;
    polygon: any;
    bounds: {
        nw: [number, number];
        se: [number, number];
    };
    ranch: {
        id: number;
        name: string;
    };
    appraisers: string[];
    using_original_polygon?: boolean;
}

export interface UpdateClaimPolygonResponse {
    message: string | null;
    ranch_area: number;
    force_generate_satellital_images?: boolean;
    centroid?: {
        latitude: number;
        longitude: number;
    };
    polygon?: FeatureCollection;
    using_original_polygon?: boolean;
    original_polygon_date?: string;
}

export const ClaimsApi = {
    //Lista de siniestros del usuario
    getClaimsList: (
        cancelToken: CancelTokenSource,
        queryParams: TablePaginatedFilters
    ): Promise<{
        data: ClaimItem[],
        count: number,
        next: string | null,
        previous: string | null,
        totalPages: number

    }> => {
        let url = `/claim/list/`;
        return Http.get(
            url, {
                cancelToken: cancelToken.token,
                params: queryParams,
                paramsSerializer: params => {
                    return parseParametersToSendBackend(params)
                }

            }
        ).then(response => response.data)
    },

    //Detalle de un siniestro específico
    getClaimDetail: (claimId: number): Promise<ClaimDetailResponse> => {
        let url = `${EndPoints.ClaimsUrls.DETAIL_INFO}`;
        return Http.get(url, {
            params: getParams([{ key: TypeParams.ID, value: claimId }])
        }).then(response => response.data)
    },

    //Listado de tasadores
    getAppraisersList: (): Promise<any> => {
        let url = `${EndPoints.ClaimsUrls.APPRAISERS}`;
        return Http.get(url).then(response => response.data)
    },

    //Modificar polígono de un claim
    updateClaimPolygon: (
        ranchId: number, 
        claimId: number,
        polygon: google.maps.LatLngLiteral[][][][]
    ): Promise<UpdateClaimPolygonResponse> => {
        let url = `${EndPoints.ClaimsUrls.UPDATE_POLYGON}`;
        return Http.patch(url, {
            ranch_id: ranchId,
            id: claimId,
            polygon: polygon
        }).then(response => response.data)
    },

    restoreClaimPolygon: (
        ranchId: number,
        claimId: number,
    ): Promise<UpdateClaimPolygonResponse> => {
        const url = `${EndPoints.ClaimsUrls.RESTORE_POLYGON}`;
        return Http.patch(url, {
            ranch_id: ranchId,
            id: claimId,
        }).then(response => response.data);
    },

    //Crear item desde polígonos seleccionados (split ranch). Mismo cuerpo que updateClaimPolygon + new_item_id.
    postRanchSplit: (
        ranchId: number,
        claimId: number,
        polygon: google.maps.LatLngLiteral[][][][],
        new_item_id: string
    ): Promise<{ message?: string; force_generate_satellital_images?: boolean }> => {
        const url = `${EndPoints.ClaimsUrls.RANCH_SPLIT}`;
        return Http.post(url, {
            ranch_id: ranchId,
            id: claimId,
            polygon: polygon,
            new_item_id: new_item_id
        }).then(response => response.data)
    },

    //Detalle de item con lista de siniestros
    getItemWithSinistersInfo: (): Promise<ItemInfo> => {
        let url = `${EndPoints.ClaimsUrls.DETAIL_INFO}`;
        return Http.get(url).then(response => response.data)
    },

    //Notas de un siniestro
    getSinisterNotes: (id: number): Promise<{
        ranch_notes: Note[]
    }> => {
        let url = `${EndPoints.ClaimsUrls.SINISTER_NOTES}`;
        return Http.get(url, {
            params: getParams([{ key: TypeParams.ID, value: id }])
        }).then(response => response.data)
    },

    //Agregar nota a un siniestro
    postNote: (claim_id: number, note: Note): Promise<{
        message: string
    }> => {
        let url = `${EndPoints.ClaimsUrls.SINISTER_NOTES}`;
        return Http.post(url, {
            claim_id,
            ...note
        }).then(response => response.data)
    },

    patchNote: (note: Note): Promise<{
        message: string
    }> => {
        let url = `${EndPoints.ClaimsUrls.SINISTER_NOTES}`;
        return Http.patch(url, {
            ...note
        }, {
            params: getParams([{ key: TypeParams.ID, value: note.id }])
        }).then(response => response.data)
    },

    deleteNote: (id: number): Promise<any> => {
        let url = `${EndPoints.ClaimsUrls.SINISTER_NOTES}`;
        return Http.delete(url, {
            params: getParams([{ key: TypeParams.ID, value: id }])
        }).then(response => response.data)
    },

    //---Satelital---------------------------
     getSatellitalImages: (id: number): Promise<{
        images: SatellitalImagesApiList,
        values: SatellitalValues
     }> => {
        let url = `${EndPoints.ClaimsUrls.SATELLITAL_IMAGES}`;
        return Http.get(url, {
            params: getParams([{ key: TypeParams.ID, value: id }])
        }).then(response => response.data)
    },

    //---Capas satelitales
     getSatellitalLayers: (id: number): Promise<SatellitalLayersInfo> => {
        let url = `${EndPoints.ClaimsUrls.MAP_LAYERS}`;
        return Http.get(url, {
            params: getParams([{ key: TypeParams.ID, value: id }])
        }).then(response => response.data)
    },

    //Valores - grafico satelital
    getSatellitialVariablesWithValues: (id): Promise<RanchVariablesCategorized> => {
        const url = `${EndPoints.ClaimsUrls.SATELLITAL_VALUES}`;
        return Http.get(url, {
            params: getParams([
            { key: 'id', value: id }
            ])
        }).then(response => response.data);
    },

    //Estadisticas - grafico satelital
    getStatsForSatellitalChart: (id, cancelToken:any, datetime_from?: string, datetime_to?: string): Promise<SatellitalChartStatistics> => {
        const url = `${EndPoints.ClaimsUrls.STATS_SATELLITAL_VALUES}`;
        return Http.get(url, {
            cancelToken: cancelToken.token,
            params: getParams([
                { key: TypeParams.ID, value: id },
                ...(datetime_from ? [{ key: TypeParams.DATE_FROM, value: datetime_from }] : []),
                ...(datetime_to ? [{ key: TypeParams.DATE_TO, value: datetime_to }] : []),
            ])
        }).then(response => response.data)
    },

    //Zonificar lote
    postZonification: (config: {
        id: number,
        satellital_image_id: number
    }): Promise<{
        message: string,
        zonified: ZonificationInfo
    }> => {
        return Http.post(EndPoints.ClaimsUrls.ZONIFICATION, config).then(response => response.data)
    },


    //Ndvi Diff
    postNdviDiff: (config: {
        id: number,
        initial_image_id: number,
        final_image_id: number
    }): Promise<{
        message: string,
        ndvi: NDVIInfo
    }> => {
        return Http.post(EndPoints.ClaimsUrls.NDVI_DIFF, config).then(response => response.data)
    },
};
